import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Cpu,
  CheckCircle2,
  FileText,
  Settings,
  Rocket,
  Building2,
  ChevronDown,
  Bell,
  Menu,
  X,
  Layers,
  Zap,
  LogOut,
  BookOpen, TrendingUp,
} from 'lucide-react';
import { PORTCOS } from '../mock/platformData';
import { usePortCo } from '../context/PortCoContext';
import { useAuth } from '../context/AuthContext';
import { rcmApi } from '../api/rcmApi';
import { API_BASE, apiFetch } from '../api/base';
import { AskMeri } from './AskMeri';


function usePendingCount(portcoId: string = 'all') {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const res = await rcmApi.getApprovals(portcoId);
        if (mounted) setCount(res?.approvals?.length ?? 0);
      } catch {
        if (mounted) setCount(0);
      }
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [portcoId]);
  return count;
}

interface FleetSummary {
  activeAgents: number;
  runningNow: number;
  pendingApprovals: number;
  actionsToday: number;
  tokensUsedToday: number;
}

function useFleetStatus(portcoId: string = 'all') {
  const [fleet, setFleet] = useState<FleetSummary | null>(null);
  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const res = await apiFetch(`${API_BASE}/fleet?portcoId=${encodeURIComponent(portcoId)}`);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (mounted && data?.summary) setFleet(data.summary);
      } catch {
        if (mounted) setFleet(null);
      }
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [portcoId]);
  return fleet;
}

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/workstreams', label: 'Workstreams', icon: Cpu },
  { to: '/approvals', label: 'Approvals', icon: CheckCircle2 },
  { to: '/audit', label: 'Audit Log', icon: FileText },
  { to: '/onboarding', label: 'Onboard', icon: Rocket },
  { to: '/entitlements', label: 'Firms & Access', icon: Building2 },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedPortco, setSelectedPortco, selectedPortcoName, portcos, canSeeAll } = usePortCo();
  const { user, logout } = useAuth();
  const roleLabel = user?.role === 'portfolio_admin' ? 'Portfolio Admin' : 'Business Admin';
  // Onboarding a new business is a portfolio-level action — hide it from tenant admins
  const navItems = NAV_ITEMS.filter(
    (item) => item.to !== '/onboarding' || user?.role === 'portfolio_admin'
  ).filter(
    (item) => item.to !== '/entitlements' || user?.role === 'portfolio_admin'
  );
  const initials = (user?.name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [portcoDropdown, setPortcoDropdown] = useState(false);
  const location = useLocation();
  const pendingCount = usePendingCount(selectedPortco);
  const fleet = useFleetStatus(selectedPortco);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans antialiased text-slate-800">
      {/* Top Header Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 px-4 lg:px-6 py-1.5 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Toggle Menu"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Meridian Logo"
              className="h-14 w-auto max-w-[220px] object-contain shrink-0"
            />
            <div className="hidden sm:block leading-tight">
              <span className="text-base font-bold text-slate-800 tracking-tight block">
                Meridian
              </span>
              <span className="text-[10px] font-semibold text-mantis-600 tracking-wider uppercase block">
                Operations
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* PortCo Selector Dropdown (portfolio admins only; tenant admins are locked) */}
          <div className="relative">
            {!canSeeAll ? (
              <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 min-w-[160px]">
                <Building2 className="w-4 h-4 text-mantis-600 mr-2 shrink-0" />
                <span className="hidden sm:inline font-medium truncate">{selectedPortcoName}</span>
              </div>
            ) : (
            <>
            <button
              onClick={() => setPortcoDropdown(!portcoDropdown)}
              className="flex items-center bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 transition-colors min-w-[160px]"
            >
              {selectedPortco === 'all' ? (
                <Layers className="w-4 h-4 text-mantis-600 mr-2 shrink-0" />
              ) : (
                <Building2 className="w-4 h-4 text-mantis-600 mr-2 shrink-0" />
              )}
              <span className="hidden sm:inline font-medium truncate">{selectedPortcoName}</span>
              <ChevronDown className="w-3.5 h-3.5 ml-1.5 text-slate-400 shrink-0" />
            </button>

            {portcoDropdown && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden py-1">
                <div className="px-3 py-1.5 border-b border-slate-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Portfolio Filter
                  </span>
                </div>

                <button
                  onClick={() => {
                    setSelectedPortco('all');
                    setPortcoDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${
                    selectedPortco === 'all' ? 'bg-mantis-50 text-mantis-700 font-medium' : 'text-slate-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Layers className="w-3.5 h-3.5 text-mantis-600" />
                    <div>
                      <div className="font-semibold">All Businesses</div>
                      <div className="text-[10px] text-slate-400">Show all PortCos ({portcos.length})</div>
                    </div>
                  </div>
                  {selectedPortco === 'all' && (
                    <CheckCircle2 className="w-4 h-4 text-mantis-500" />
                  )}
                </button>

                <div className="border-t border-slate-100 my-0.5" />

                {portcos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setSelectedPortco(p.id);
                      setPortcoDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${
                      selectedPortco === p.id ? 'bg-mantis-50 text-mantis-700 font-medium' : 'text-slate-700'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${PORTCOS.find((x) => x.id === p.id)?.status === 'active' ? 'bg-mantis-500' : 'bg-slate-300'}`} />
                      <div>
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-[10px] text-slate-400">{p.region} • {p.code}</div>
                      </div>
                    </div>
                    {selectedPortco === p.id && (
                      <CheckCircle2 className="w-4 h-4 text-mantis-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
            </>
            )}
          </div>

          {/* Notification Bell */}
          <button
            className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 border border-slate-200 transition-colors"
            aria-label="Notifications"
            onClick={() => window.location.hash = '#/approvals'}
          >
            <Bell className="w-4 h-4" />
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-redwood-500 text-white text-[9px] font-bold flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </button>

          <div className="h-5 w-px bg-slate-200 hidden sm:block" />

          {/* User Profile */}
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-full bg-mantis-500 text-white font-bold text-xs flex items-center justify-center">
              {initials}
            </div>
            <div className="hidden lg:block text-left">
              <span className="text-xs font-semibold text-slate-800 block leading-tight">
                {user?.name ?? 'Signed In'}
              </span>
              <span className="text-[10px] text-slate-500 block leading-tight">
                {roleLabel}
              </span>
            </div>
            <button
              onClick={() => logout()}
              title="Sign out"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-redwood-600 border border-slate-200 transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Body Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'fixed inset-y-0 left-0 z-40 w-56 bg-white pt-16' : 'hidden'
          } lg:relative lg:pt-0 lg:flex lg:w-56 bg-white border-r border-slate-200 p-3 flex-col shrink-0 space-y-1`}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.to
              : location.pathname === item.to || location.pathname.startsWith(item.to + '/');

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => setSidebarOpen(false)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-mantis-50 text-mantis-700 border border-mantis-200 font-semibold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <div className="flex items-center space-x-2.5">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-mantis-600' : 'text-slate-400'}`} />
                  <span>{item.label}</span>
                </div>
                {item.label === 'Approvals' && pendingCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-redwood-50 text-redwood-600 border border-redwood-200">
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            );
          })}

          {/* Value Report — monthly per-business report */}
          <NavLink
            to="/value-report"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
              isActive
                ? 'bg-mantis-50 text-mantis-700 border border-mantis-200 font-semibold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
            }`}
          >
            <div className="flex items-center space-x-2.5">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              <span>Value Report</span>
            </div>
          </NavLink>

          {/* User Guide — opens the plain-English guide in a new tab */}
          <a
            href="/user-guide.html"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setSidebarOpen(false)}
            className="w-full flex items-center px-3 py-2.5 rounded-lg text-xs font-medium transition-colors text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent"
          >
            <div className="flex items-center space-x-2.5">
              <BookOpen className="w-4 h-4 text-slate-400" />
              <span>User Guide</span>
            </div>
          </a>

          {/* Agent Status Box */}
          <div className="mt-auto pt-4 border-t border-slate-100">
            <div className="bg-slate-50 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Agent Fleet</span>
                <span className={`w-2 h-2 rounded-full ${(fleet?.runningNow ?? 0) > 0 ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'}`} />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Active Agents</span>
                  <span className="text-mantis-600 font-medium">{fleet ? `${fleet.activeAgents} active` : '…'}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Running Now</span>
                  <span className="text-blue-600 font-medium">{fleet ? `${fleet.runningNow} running` : '…'}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Pending Approvals</span>
                  <span className="text-amber-600 font-medium">{fleet ? `${fleet.pendingApprovals} awaiting` : '…'}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Actions Today</span>
                  <span className="text-mantis-600 font-medium">{fleet ? `${fleet.actionsToday} completed` : '…'}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">LLM Tokens Today</span>
                  <span className="text-slate-600 font-medium">{fleet ? fleet.tokensUsedToday.toLocaleString() : '…'}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
        <AskMeri />
      </div>
    </div>
  );
};
